/**
 * Domain request schemas — shared between the API (server-side request
 * validation) and the web SPA (client-side form validation).
 *
 * Spec §6 Domain Routing, §19 Domain:
 *   - `hostname` is globally unique among non-soft-deleted rows. The active
 *     form is enforced by a partial unique index on `WHERE deletedAt IS NULL`
 *     (see `prisma/migrations/0001_init/migration.sql`). We never declare
 *     `@unique` in the Prisma schema for `hostname`, so all lookups go through
 *     `findFirst({ where: { hostname, deletedAt: null } })`.
 *   - `hostname` is normalised at the API boundary (lowercase, trim, strip
 *     port, strip trailing dot — see `normaliseHostname` in
 *     `api/src/middleware/hostnameRouter.ts`). We do NOT pre-normalise inside
 *     the schema: normalisation is a runtime concern that depends on a single
 *     canonical implementation reused by both routing and storage. The schema
 *     here only validates structural constraints (length, allowed characters).
 *   - `isPrimary` is a per-site boolean. Only one primary `Domain` may exist
 *     per site among active rows — enforced by the partial unique index
 *     `domain_site_primary_active_unique`.
 *   - The reserved `APP_HOSTNAME` must never be assignable as a `Domain`
 *     for any site. The service rejects this with 422 before any database
 *     write.
 *
 * Hostname max length is 253 characters per RFC 1035 §2.3.4 (the absolute
 * upper bound for a fully-qualified domain name including all labels and
 * separators). We do NOT enforce label-by-label limits here — Postgres-level
 * uniqueness and DNS resolution will reject anything longer; the schema is a
 * cheap structural guard.
 *
 * Hostname character set:
 *   - lowercase letters, digits, hyphens, and dots,
 *   - no leading/trailing dot or hyphen at the boundaries,
 *   - punycode-compatible (raw ASCII; IDN inputs must be punycoded by the
 *     caller per spec §6 Hostname Normalisation).
 */

import { z } from "zod";

/**
 * Hostname regex — permissive ASCII / punycode form. The `normaliseHostname`
 * helper in `api/src/middleware/hostnameRouter.ts` handles case folding, port
 * stripping, and trailing-dot trimming at runtime, so by the time a value
 * reaches the database it has already been normalised. The regex below is the
 * structural guard applied by Zod *before* normalisation — we accept mixed
 * case AND a trailing dot here, then the service lowercases / strips them.
 *
 *   - one or more dot-separated labels,
 *   - each label: alphanumeric, optionally with internal hyphens,
 *   - each label is 1–63 chars (DNS spec),
 *   - optional trailing dot (FQDN form — accepted, normaliser strips it),
 *   - port suffix (`:NNNN`) is also accepted — the normaliser strips it.
 *     Without this allowance, an operator's "host:port" copy-paste would
 *     fail validation with a confusing 422 instead of being silently
 *     normalised. Bracketed IPv6 hosts (`[::1]:3000`) follow the same
 *     accept-then-strip rule.
 *   - the whole hostname is 1–253 chars (constrained at the schema level
 *     below in `.max(253)`).
 *
 * Single-label hosts (e.g. `localhost`, `app`) are valid — useful for dev
 * environments and the app-host comparison.
 */
export const HOSTNAME_REGEX =
  /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.?(:\d{1,5})?$/;

export const hostnameSchema = z
  .string()
  .trim()
  .min(1, "Hostname is required")
  .max(253, "Hostname must be at most 253 characters")
  .regex(HOSTNAME_REGEX, "Hostname must be a valid DNS name");

/**
 * Body schema for `POST /api/admin/sites/:siteId/domains`.
 *
 * `.strict()` rejects unknown top-level keys so a typo like `IsPrimary`
 * surfaces as a 422 instead of being silently dropped.
 */
export const createDomainSchema = z
  .object({
    hostname: hostnameSchema,
    isPrimary: z.boolean().default(false),
  })
  .strict();
export type CreateDomainInput = z.infer<typeof createDomainSchema>;

/**
 * Body schema for `PATCH /api/admin/domains/:id`. Only `isPrimary` is mutable
 * post-create at MVP — to change a hostname the operator deletes the domain
 * and creates a new one (which keeps the audit trail crisp and avoids
 * complications around the active-hostname uniqueness index).
 */
export const updateDomainSchema = z
  .object({
    isPrimary: z.boolean().optional(),
  })
  .strict()
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "Update must include at least one field",
  });
export type UpdateDomainInput = z.infer<typeof updateDomainSchema>;

/**
 * Query schema for `GET /api/admin/sites/:siteId/domains`. Mirrors the
 * sites/pages list-query shape (page default 1, pageSize default 20, max 100).
 * Standards/api-patterns.md requires every list endpoint to be paginated
 * with a server-enforced ceiling — `pageSize` cannot exceed 100 (#59).
 */
export const listDomainsQuerySchema = z
  .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
  })
  .strict();
export type ListDomainsQuery = z.infer<typeof listDomainsQuerySchema>;

/**
 * Public-facing Domain shape. ISO-string timestamps for transport. Mirrors the
 * `SiteResponse` shape in `sites.ts`.
 */
export type DomainResponse = {
  id: string;
  siteId: string;
  hostname: string;
  isPrimary: boolean;
  status: "ACTIVE" | "INACTIVE" | "PENDING_VERIFICATION";
  // ISO timestamp of the most recent successful DNS verification; null on rows
  // that have never been verified (custom-domain P1, issue #291).
  verifiedAt: string | null;
  createdAt: string;
};

/**
 * Server-info response (custom-domain P3, issue #293).
 *
 * Small bag of server-side values the admin SPA needs to render
 * customer-facing copy (e.g. "Point your A record to <vpsPublicIp>").
 * Lives on the shared schema barrel because the admin pages that consume
 * it sit on the same `data:` envelope as every other admin resource.
 */
export type ServerInfoResponse = {
  vpsPublicIp: string;
};

/**
 * Verify-domain endpoint response (custom-domain P2, issue #292).
 *
 * Discriminated by `ok`. On success the caller flips to ACTIVE and the
 * caller can re-render the updated domain row from `domain`. On a miss the
 * caller renders the diff (`expectedIp` vs the `observedIps` actually
 * returned by DNS) so the operator can debug at the resolver layer without
 * a round-trip to the server.
 */
export type VerifyDomainResponse =
  | { ok: true; domain: DomainResponse }
  | {
      ok: false;
      reason: "DNS_MISMATCH" | "DNS_LOOKUP_FAILED";
      expectedIp: string;
      observedIps: string[];
      message: string;
    };
